import React, { useCallback } from 'react';
import { getIn, useField, useFormikContext } from 'formik';
import { ISelectOption, ISelectProps, Select } from '../Select';
import isNil from 'lodash/isNil';
import { ActionMeta } from 'react-select';

export interface ISelectFieldProps extends ISelectProps {
  name: string;
  equalityCheck?: (x: any, y: any) => boolean;
}

const getValueFromFormikErrors = (formik: any, name: any) => {
  // Split the name string by '.'
  const pathParts = name.split('.');

  // Get the last part and cast it to a number
  const lastPart = pathParts[pathParts.length - 1];
  const lastPartAsNumber = Number(lastPart);

  // Check if the last part is a number
  if (!isNaN(lastPartAsNumber)) {
    // If it is a number, remove it from the path and use getIn to get the array
    const pathWithoutNumber = pathParts.slice(0, -1).join('.');
    const errorMessage = getIn(formik.errors, pathWithoutNumber);

    // Access the array using the last part as an index
    return errorMessage ?? undefined;
  } else {
    // If the last part is not a number, just use getIn directly
    return getIn(formik.errors, name)?.toString();
  }
};

//FYI: this simplifies integration with Formik. See README
export const SelectField: React.FC<ISelectFieldProps> = ({
  name,
  equalityCheck = (x, y) => x === y,
  onChange: onChangePropCallback,
  ...rest
}) => {
  const [field, meta, helpers] = useField<ISelectOption>({ name });
  const formik = useFormikContext();
  const onChange = useCallback(
    async (value: ISelectOption, x: ActionMeta<ISelectOption>) => {
      await helpers.setTouched(true);
      await helpers.setValue(value?.value);
      if (onChangePropCallback) {
        onChangePropCallback(value?.value, x);
      }
    },
    [onChangePropCallback, helpers]
  );
  const errorText =
    (!!formik.submitCount || meta.touched) && meta.error ? getValueFromFormikErrors(formik, name) : null;

  const value = (rest.options ?? []).find((o) => equalityCheck(o.value, field.value)) ?? null;
  return (
    <Select
      {...rest}
      onChange={onChange}
      errorText={isNil(rest.errorText) ? errorText : rest.errorText}
      value={value}
    />
  );
};

export const MultipleSelectField: React.FC<ISelectFieldProps> = ({ name, ...rest }) => {
  const formik = useFormikContext();
  const [field, meta, helpers] = useField<ISelectOption[]>({ name });
  const onSelectChange = useCallback(
    async (values: ISelectOption[]) => {
      const data = values.map((value) => value.value);
      await helpers.setTouched(true);
      await helpers.setValue(data);
    },
    [helpers]
  );

  const errorText = (!!formik.submitCount || meta.touched) && meta.error ? getIn(formik.errors, name) : null;

  const value: ISelectOption[] =
    field.value && field.value.map((value: any) => (rest.options || []).find((option) => option.value === value));

  return (
    <Select
      {...rest}
      value={value}
      errorText={errorText}
      onChange={onSelectChange}
      isMulti
      data-test-id={rest['data-test-id'] || name}
    />
  );
};
